import {
  type Address, type Anamnesis, type AnamnesisAnswers, anamnesisAnswersSchema, anamnesisRedFlags, CHRONIC_CONDITIONS, CLASSIFICATIONS,
  type ClassificationSystem, type EmergencyContact, type Evaluation, type EvaluationFields, HAIR_TREATMENTS, INDICATIONS, type Patient,
  PROCEDURE_AREAS, RED_FLAGS, SEX_AT_BIRTH, TECHNIQUES, type TimelineItem,
} from '@dnx/contracts';
import { formatCpf } from '@dnx/domain';
import { Alert, Badge, Button, Card, CardHeader, Dialog, Field, Input, Select, Spinner, Tabs, cn } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft, CalendarDays, ClipboardList, FilePlus2, FileSignature, Lock, MessageSquarePlus, Pencil, ScanFace, ShieldCheck, Stethoscope, Trash2, TriangleAlert,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import { PatientQuotes } from '../billing/patient-quotes.js';
import { fieldErrors } from './patients-page.js';
import { PhotosTab } from './photos-tab.js';
import { PatientDocumentsTab } from '../documents/patient-documents.js';
import { SurgeryTab } from '../surgery/surgery-tab.js';
import { anamnesisQuery, day, evaluationQuery, formatDate, patientQuery, recordsQuery, SEX_LABELS, timelineQuery, when } from './queries.js';

export type PatientTab = 'resumo' | 'anamnese' | 'avaliacao' | 'fotos' | 'documentos' | 'cirurgia';

function usePerms() {
  const { data: me } = useSuspenseQuery(meQuery);
  const p = new Set(me.permissions);
  return { write: p.has('clinical.record.write'), sign: p.has('clinical.record.sign') };
}

function invalidatePatient(qc: ReturnType<typeof useQueryClient>, id: string) {
  void qc.invalidateQueries({ queryKey: ['patients'] });
  void qc.invalidateQueries({ queryKey: ['patients', id] });
}

function RedFlags({ flags, className }: { flags: string[]; className?: string }) {
  if (!flags.length) return null;
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {flags.map((f) => <Badge key={f} tone="warning"><TriangleAlert className="size-3" />{RED_FLAGS[f as keyof typeof RED_FLAGS] ?? f}</Badge>)}
    </div>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12px] font-medium tracking-wide text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg">{children || <span className="text-fg-subtle">—</span>}</dd>
    </div>
  );
}

// ------------------------------------------------------------------ cadastro
function EditPatientDialog({ p, onClose }: { p: Patient; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    cpf: p.cpf ? formatCpf(p.cpf) : '', birthDate: p.birthDate ?? '', sexAtBirth: p.sexAtBirth ?? '',
    address: p.address ?? {} as Address, emergency: p.emergencyContact ?? { name: '', phone: '' } as EmergencyContact,
  });
  const save = useMutation({
    mutationFn: () => api<Patient>(`/patients/${p.id}`, {
      method: 'PATCH',
      body: {
        cpf: f.cpf || null, birthDate: f.birthDate || null, sexAtBirth: f.sexAtBirth || null,
        address: Object.values(f.address).some(Boolean) ? f.address : null,
        emergencyContact: f.emergency.name ? f.emergency : null,
      },
    }),
    onSuccess: (np) => { qc.setQueryData(['patients', p.id], np); invalidatePatient(qc, p.id); onClose(); },
  });
  const errs = fieldErrors(save.error);
  const setAddr = (k: keyof Address, v: string) => setF({ ...f, address: { ...f.address, [k]: v } });
  return (
    <Dialog open onClose={onClose} title="Cadastro do paciente" variant="drawer">
      <form className="grid gap-4 p-5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="CPF" error={errs.cpf}><Input value={f.cpf} onChange={(e) => setF({ ...f, cpf: formatCpf(e.target.value) })} inputMode="numeric" placeholder="000.000.000-00" /></Field>
          <Field label="Nascimento" error={errs.birthDate}><Input type="date" value={f.birthDate} onChange={(e) => setF({ ...f, birthDate: e.target.value })} /></Field>
        </div>
        <Field label="Sexo biológico">
          <Select value={f.sexAtBirth} onChange={(e) => setF({ ...f, sexAtBirth: e.target.value })}>
            <option value="">—</option>
            {SEX_AT_BIRTH.map((s) => <option key={s} value={s}>{SEX_LABELS[s]}</option>)}
          </Select>
        </Field>
        <p className="mt-2 text-[13px] font-semibold text-fg">Endereço</p>
        <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
          <Field label="CEP"><Input value={f.address.zip ?? ''} onChange={(e) => setAddr('zip', e.target.value)} inputMode="numeric" maxLength={9} /></Field>
          <Field label="Rua e número"><Input value={f.address.street ?? ''} onChange={(e) => setAddr('street', e.target.value)} /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-[1fr_90px]">
          <Field label="Cidade"><Input value={f.address.city ?? ''} onChange={(e) => setAddr('city', e.target.value)} /></Field>
          <Field label="UF"><Input value={f.address.state ?? ''} onChange={(e) => setAddr('state', e.target.value.toUpperCase().slice(0, 2))} /></Field>
        </div>
        <p className="mt-2 text-[13px] font-semibold text-fg">Contato de emergência</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome"><Input value={f.emergency.name} onChange={(e) => setF({ ...f, emergency: { ...f.emergency, name: e.target.value } })} /></Field>
          <Field label="Telefone"><Input value={f.emergency.phone} onChange={(e) => setF({ ...f, emergency: { ...f.emergency, phone: e.target.value } })} inputMode="tel" /></Field>
        </div>
        <Field label="Parentesco"><Input value={f.emergency.relation ?? ''} onChange={(e) => setF({ ...f, emergency: { ...f.emergency, relation: e.target.value } })} /></Field>
        {save.error && !Object.keys(errs).length && <Alert tone="danger">{save.error.message}</Alert>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" loading={save.isPending}>Salvar</Button>
        </div>
      </form>
    </Dialog>
  );
}

const TL_ICON: Record<TimelineItem['kind'], typeof ClipboardList> = {
  patient_created: FilePlus2, anamnesis: ClipboardList, evaluation: ScanFace, addendum: MessageSquarePlus, appointment: CalendarDays,
};

function Summary({ p, onOpen }: { p: Patient; onOpen: (tab: PatientTab) => void }) {
  const { write } = usePerms();
  const [editing, setEditing] = useState(false);
  const { data: timeline } = useQuery(timelineQuery(p.id));
  const addr = p.address ? [p.address.street, p.address.city && `${p.address.city}${p.address.state ? `/${p.address.state}` : ''}`, p.address.zip].filter(Boolean).join(' · ') : '';
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="grid content-start gap-5">
      <Card>
        <CardHeader title="Cadastro" description="CPF, nascimento, endereço e contato de emergência ficam cifrados."
          action={write && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}><Pencil className="size-3.5" />Editar</Button>} />
        <dl className="grid gap-5 p-5 sm:grid-cols-2">
          <Info label="CPF">{p.cpf && formatCpf(p.cpf)}</Info>
          <Info label="Nascimento">{p.birthDate && `${formatDate(p.birthDate)} (${p.age} anos)`}</Info>
          <Info label="Sexo biológico">{p.sexAtBirth && SEX_LABELS[p.sexAtBirth]}</Info>
          <Info label="Celular">{p.phoneLast4 && `final ${p.phoneLast4}`}</Info>
          <Info label="Endereço">{addr}</Info>
          <Info label="Emergência">{p.emergencyContact && `${p.emergencyContact.name}${p.emergencyContact.relation ? ` (${p.emergencyContact.relation})` : ''} · ${p.emergencyContact.phone}`}</Info>
        </dl>
      </Card>
      <PatientQuotes patientId={p.id} />
      </div>
      <Card className="h-fit">
        <CardHeader title="Linha do tempo" />
        {!timeline ? <Spinner className="m-5 size-4 text-fg-subtle" label="Carregando" /> : (
          <ol className="relative grid gap-0 px-5 py-4">
            {timeline.map((t, i) => {
              const Icon = TL_ICON[t.kind];
              const tab: PatientTab | null = t.kind === 'anamnesis' || (t.kind === 'addendum' && t.status === 'anamnesis') ? 'anamnese'
                : t.kind === 'evaluation' || t.kind === 'addendum' ? 'avaliacao' : null;
              return (
                <li key={`${t.kind}-${t.refId}-${i}`} className="relative grid grid-cols-[28px_1fr] gap-3 pb-5 last:pb-0">
                  {i < timeline.length - 1 && <span aria-hidden className="absolute top-7 bottom-0 left-[13px] w-px bg-border" />}
                  <span className={cn('grid size-7 place-items-center rounded-full border bg-surface', t.status === 'signed' ? 'border-success/40 text-success' : 'border-border text-fg-muted')}>
                    <Icon className="size-3.5" />
                  </span>
                  <div className="min-w-0 pt-0.5">
                    {tab ? <button type="button" onClick={() => onOpen(tab)} className="text-left text-sm font-medium text-fg hover:text-primary">{t.title}</button>
                      : <p className="text-sm font-medium text-fg">{t.title}</p>}
                    <p className="text-[12.5px] text-fg-muted">{when(t.at)}{t.detail && ` · ${t.detail}`}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Card>
      {editing && <EditPatientDialog p={p} onClose={() => setEditing(false)} />}
    </div>
  );
}

// ------------------------------------------------------------------ registros (lista lateral compartilhada)
function RecordPicker({ items, value, onChange, onNew, label, canCreate }: {
  items: { id: string; status: 'draft' | 'signed'; createdAt: string }[]; value: string | null; onChange: (id: string) => void;
  onNew: () => void; label: string; canCreate: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {items.map((r) => (
        <button key={r.id} type="button" onClick={() => onChange(r.id)} aria-pressed={r.id === value}
          className={cn('inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px]',
            r.id === value ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted hover:text-fg')}>
          {r.status === 'signed' ? <Lock className="size-3" /> : <Pencil className="size-3" />}
          {day(r.createdAt)}{r.status === 'draft' && ' · rascunho'}
        </button>
      ))}
      {canCreate && <Button size="sm" variant="secondary" onClick={onNew}><FilePlus2 className="size-3.5" />{label}</Button>}
    </div>
  );
}

function Addenda({ entity, id, items, onAdded }: { entity: 'anamneses' | 'evaluations'; id: string; items: Anamnesis['addenda']; onAdded: () => void }) {
  const { write } = usePerms();
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () => api<void>(`/${entity}/${id}/addenda`, { method: 'POST', body: { text } }),
    onSuccess: () => { setText(''); onAdded(); },
  });
  return (
    <Card>
      <CardHeader title="Adendos" description="O registro assinado não muda. Correções e complementos entram aqui, com autor e data." />
      <div className="grid gap-3 p-5">
        {items.map((a) => (
          <div key={a.id} className="rounded-md border border-border bg-surface-muted/50 px-3.5 py-3">
            <p className="text-sm whitespace-pre-wrap text-fg">{a.text}</p>
            <p className="mt-1 text-[12px] text-fg-muted">{a.authorName} · {when(a.createdAt)}</p>
          </div>
        ))}
        {!items.length && !write && <p className="text-sm text-fg-muted">Nenhum adendo.</p>}
        {write && (
          <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
            <textarea aria-label="Novo adendo" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={4000} placeholder="Escreva o adendo…"
              className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary" />
            {add.error && <Alert tone="danger">{add.error.message}</Alert>}
            <div><Button size="sm" type="submit" disabled={text.trim().length < 3} loading={add.isPending}>Registrar adendo</Button></div>
          </form>
        )}
      </div>
    </Card>
  );
}

function SignedBanner({ by, at }: { by: string | null; at: string | null }) {
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-success/25 bg-success-soft px-3.5 py-2.5 text-sm text-fg">
      <ShieldCheck className="size-4 text-success" />Assinado por <strong>{by ?? '—'}</strong> em {at && when(at)}. Não pode mais ser alterado.
    </div>
  );
}

function DraftActions({ dirty, saving, onSave, onSign, signing, onDelete, canSign, signHint }: {
  dirty: boolean; saving: boolean; onSave: () => void; onSign: () => void; signing: boolean; onDelete: () => void; canSign: boolean; signHint: string;
}) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button onClick={onSave} disabled={!dirty} loading={saving}>{dirty ? 'Salvar rascunho' : 'Rascunho salvo'}</Button>
      {canSign && <Button variant="secondary" onClick={() => setConfirm(true)} disabled={dirty}><FileSignature className="size-4" />Assinar</Button>}
      {!canSign && <span className="text-[12.5px] text-fg-muted">A assinatura é do médico.</span>}
      <Button variant="ghost" className="ml-auto" onClick={onDelete}><Trash2 className="size-4" />Descartar rascunho</Button>
      <Dialog open={confirm} onClose={() => setConfirm(false)} title="Assinar e fechar o registro?">
        <div className="grid gap-4 p-5 text-sm text-fg-muted">
          <p>{signHint} Depois de assinado, o registro fica no prontuário e não pode ser editado nem apagado; correções entram como adendo.</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirm(false)}>Voltar</Button>
            <Button loading={signing} onClick={() => { onSign(); setConfirm(false); }}><FileSignature className="size-4" />Assinar</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------------ anamnese
const CONDITION_LABELS: Record<(typeof CHRONIC_CONDITIONS)[number], string> = {
  diabetes: 'Diabetes', hypertension: 'Hipertensão', cardiac: 'Doença cardíaca', autoimmune: 'Doença autoimune', thyroid: 'Tireoide',
  infectious: 'Hepatite / HIV / infecciosa', keloid: 'Queloide', bleeding_disorder: 'Distúrbio de coagulação',
};
const TREATMENT_LABELS: Record<(typeof HAIR_TREATMENTS)[number], string> = {
  minoxidil: 'Minoxidil', finasteride: 'Finasterida', dutasteride: 'Dutasterida', prp: 'PRP', laser: 'Laser', other: 'Outro',
};

function Chips<T extends string>({ all, value, onChange, label, disabled }: { all: readonly T[]; value: T[]; onChange: (v: T[]) => void; label: (v: T) => string; disabled?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {all.map((v) => {
        const on = value.includes(v);
        return (
          <button key={v} type="button" disabled={disabled} aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== v) : [...value, v])}
            className={cn('h-8 rounded-full border px-3 text-[13px] disabled:cursor-default', on ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted enabled:hover:text-fg')}>
            {label(v)}
          </button>
        );
      })}
    </div>
  );
}

function YesNo({ label, value, onChange, disabled, hint }: { label: string; value: boolean | null; onChange: (v: boolean | null) => void; disabled?: boolean; hint?: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-2.5">
      <div><p className="text-sm text-fg">{label}</p>{hint && <p className="text-[12px] text-fg-muted">{hint}</p>}</div>
      <div role="radiogroup" aria-label={label} className="flex rounded-md border border-border-strong p-0.5">
        {([[true, 'Sim'], [false, 'Não'], [null, '—']] as const).map(([v, l]) => (
          <button key={l} type="button" role="radio" aria-checked={value === v} disabled={disabled} onClick={() => onChange(v)}
            className={cn('h-7 min-w-11 rounded px-2.5 text-[13px]', value === v ? (v ? 'bg-warning-soft font-medium text-fg' : 'bg-surface-muted font-medium text-fg') : 'text-fg-muted')}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

const Area = ({ value, onChange, disabled, rows = 3, placeholder, label }: { value: string; onChange: (v: string) => void; disabled?: boolean; rows?: number; placeholder?: string; label: string }) => (
  <textarea aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} rows={rows} placeholder={placeholder}
    className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary disabled:bg-surface-muted/60 disabled:text-fg" />
);

function AnamnesisForm({ a, patientId, onDeleted }: { a: Anamnesis; patientId: string; onDeleted: () => void }) {
  const qc = useQueryClient();
  const { write, sign } = usePerms();
  const [ans, setAns] = useState<AnamnesisAnswers>(a.answers);
  const [dirty, setDirty] = useState(false);
  const locked = a.status === 'signed' || !write;
  const set = <K extends keyof AnamnesisAnswers>(k: K, v: AnamnesisAnswers[K]) => { setAns((x) => ({ ...x, [k]: v })); setDirty(true); };
  const refresh = (n?: Anamnesis) => { if (n) qc.setQueryData(['anamneses', a.id], n); void qc.invalidateQueries({ queryKey: ['anamneses', a.id] }); invalidatePatient(qc, patientId); };
  const save = useMutation({ mutationFn: () => api<Anamnesis>(`/anamneses/${a.id}`, { method: 'PATCH', body: ans }), onSuccess: (n) => { setDirty(false); refresh(n); } });
  const doSign = useMutation({ mutationFn: () => api<Anamnesis>(`/anamneses/${a.id}/sign`, { method: 'POST' }), onSuccess: (n) => refresh(n) });
  const del = useMutation({ mutationFn: () => api<void>(`/anamneses/${a.id}`, { method: 'DELETE' }), onSuccess: () => { invalidatePatient(qc, patientId); onDeleted(); } });
  const flags = anamnesisRedFlags(ans);
  const err = save.error ?? doSign.error ?? del.error;

  return (
    <div className="grid gap-5">
      {a.status === 'signed' ? <SignedBanner by={a.signedByName} at={a.signedAt} /> : (
        <p className="text-[13px] text-fg-muted">Rascunho de {a.createdByName ?? '—'} · atualizado {when(a.updatedAt)}</p>
      )}
      {flags.length > 0 && (
        <Alert tone="warning" title="Sinais de alerta">
          <RedFlags flags={flags} className="mt-1.5" />
        </Alert>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Queixa e histórico" />
          <div className="grid gap-4 p-5">
            <Field label="Queixa principal"><Area label="Queixa principal" value={ans.chiefComplaint} onChange={(v) => set('chiefComplaint', v)} disabled={locked} placeholder="O que incomoda e o que espera do tratamento" /></Field>
            <Field label="Há quantos anos começou a queda?">
              <Input type="number" min={0} max={80} value={ans.hairLossOnsetYears ?? ''} disabled={locked}
                onChange={(e) => set('hairLossOnsetYears', e.target.value === '' ? null : Number(e.target.value))} className="max-w-32" />
            </Field>
            <div className="divide-y divide-border">
              <YesNo label="Histórico familiar de calvície" value={ans.familyHistory} onChange={(v) => set('familyHistory', v)} disabled={locked} />
              <YesNo label="Já fez transplante capilar" value={ans.previousTransplant} onChange={(v) => set('previousTransplant', v)} disabled={locked} />
            </div>
            <Field label="Tratamentos atuais ou anteriores">
              <Chips all={HAIR_TREATMENTS} value={ans.treatments} onChange={(v) => set('treatments', v)} label={(v) => TREATMENT_LABELS[v]} disabled={locked} />
            </Field>
            <Field label="Expectativas"><Area label="Expectativas" value={ans.expectations} onChange={(v) => set('expectations', v)} disabled={locked} rows={2} /></Field>
          </div>
        </Card>
        <Card>
          <CardHeader title="Saúde" description="Respostas marcadas geram sinais de alerta para o médico." />
          <div className="grid gap-4 p-5">
            <Field label="Condições de saúde">
              <Chips all={CHRONIC_CONDITIONS} value={ans.chronicConditions} onChange={(v) => set('chronicConditions', v)} label={(v) => CONDITION_LABELS[v]} disabled={locked} />
            </Field>
            <div className="divide-y divide-border">
              <YesNo label="Usa anticoagulante ou AAS" value={ans.anticoagulants} onChange={(v) => set('anticoagulants', v)} disabled={locked} />
              <YesNo label="Alergia a anestésico local" value={ans.anestheticAllergy} onChange={(v) => set('anestheticAllergy', v)} disabled={locked} />
              <YesNo label="Tabagista" value={ans.smoker} onChange={(v) => set('smoker', v)} disabled={locked} />
              <YesNo label="Gestante ou amamentando" value={ans.pregnantOrBreastfeeding} onChange={(v) => set('pregnantOrBreastfeeding', v)} disabled={locked} />
              <YesNo label="Preocupação desproporcional com a aparência" hint="Triagem de dismorfia corporal" value={ans.appearanceDistress} onChange={(v) => set('appearanceDistress', v)} disabled={locked} />
            </div>
            <Field label="Medicamentos em uso"><Area label="Medicamentos em uso" value={ans.medications} onChange={(v) => set('medications', v)} disabled={locked} rows={2} /></Field>
            <Field label="Alergias"><Area label="Alergias" value={ans.allergies} onChange={(v) => set('allergies', v)} disabled={locked} rows={2} /></Field>
            <Field label="Observações"><Area label="Observações" value={ans.notes} onChange={(v) => set('notes', v)} disabled={locked} rows={2} /></Field>
          </div>
        </Card>
      </div>
      {err && <Alert tone="danger">{err.message}</Alert>}
      {a.status === 'draft' && write && (
        <DraftActions dirty={dirty} saving={save.isPending} onSave={() => save.mutate()} signing={doSign.isPending} onSign={() => doSign.mutate()}
          onDelete={() => del.mutate()} canSign={sign} signHint="Confira as respostas com o paciente." />
      )}
      {a.status === 'signed' && <Addenda entity="anamneses" id={a.id} items={a.addenda} onAdded={() => refresh()} />}
    </div>
  );
}

// ------------------------------------------------------------------ avaliação capilar
const SYSTEM_LABELS: Record<ClassificationSystem, string> = { norwood_hamilton: 'Norwood-Hamilton', ludwig: 'Ludwig', savin: 'Savin', none: 'Sem escala' };
const AREA_LABELS: Record<(typeof PROCEDURE_AREAS)[number], string> = { scalp: 'Couro cabeludo', eyebrow: 'Sobrancelha', beard: 'Barba', other: 'Outra' };
const INDICATION_LABELS: Record<(typeof INDICATIONS)[number], string> = { indicated: 'Indicado', indicated_with_caveats: 'Indicado com ressalvas', not_indicated: 'Não indicado' };

function EvaluationForm({ e, patientId, onDeleted }: { e: Evaluation; patientId: string; onDeleted: () => void }) {
  const qc = useQueryClient();
  const { write, sign } = usePerms();
  const [f, setF] = useState<EvaluationFields>(e.fields);
  const [dirty, setDirty] = useState(false);
  const locked = e.status === 'signed' || !write;
  const set = <K extends keyof EvaluationFields>(k: K, v: EvaluationFields[K]) => { setF((x) => ({ ...x, [k]: v })); setDirty(true); };
  const setDonor = <K extends keyof EvaluationFields['donorArea']>(k: K, v: EvaluationFields['donorArea'][K]) => set('donorArea', { ...f.donorArea, [k]: v });
  const refresh = (n?: Evaluation) => { if (n) qc.setQueryData(['evaluations', e.id], n); void qc.invalidateQueries({ queryKey: ['evaluations', e.id] }); invalidatePatient(qc, patientId); };
  const save = useMutation({ mutationFn: () => api<Evaluation>(`/evaluations/${e.id}`, { method: 'PATCH', body: f }), onSuccess: (n) => { setDirty(false); refresh(n); } });
  const doSign = useMutation({ mutationFn: () => api<Evaluation>(`/evaluations/${e.id}/sign`, { method: 'POST' }), onSuccess: (n) => refresh(n) });
  const del = useMutation({ mutationFn: () => api<void>(`/evaluations/${e.id}`, { method: 'DELETE' }), onSuccess: () => { invalidatePatient(qc, patientId); onDeleted(); } });
  const errs = { ...fieldErrors(save.error), ...fieldErrors(doSign.error) };
  const err = save.error ?? doSign.error ?? del.error;
  const grades = CLASSIFICATIONS[f.classificationSystem] as readonly string[];
  const num = (v: string) => (v === '' ? null : Number(v));

  return (
    <div className="grid gap-5">
      {e.status === 'signed' ? <SignedBanner by={e.doctorName} at={e.signedAt} /> : (
        <p className="text-[13px] text-fg-muted">Rascunho · atualizado {when(e.updatedAt)}</p>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Classificação" />
          <div className="grid gap-4 p-5">
            <Field label="Área"><Select value={f.procedureArea} disabled={locked} onChange={(x) => set('procedureArea', x.target.value as EvaluationFields['procedureArea'])}>
              {PROCEDURE_AREAS.map((v) => <option key={v} value={v}>{AREA_LABELS[v]}</option>)}
            </Select></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Escala"><Select value={f.classificationSystem} disabled={locked}
                onChange={(x) => { setF((y) => ({ ...y, classificationSystem: x.target.value as ClassificationSystem, classificationGrade: null })); setDirty(true); }}>
                {(Object.keys(SYSTEM_LABELS) as ClassificationSystem[]).map((v) => <option key={v} value={v}>{SYSTEM_LABELS[v]}</option>)}
              </Select></Field>
              {f.classificationSystem !== 'none' && (
                <Field label="Grau" error={errs.classificationGrade}><Select value={f.classificationGrade ?? ''} disabled={locked} onChange={(x) => set('classificationGrade', x.target.value || null)}>
                  <option value="">—</option>
                  {grades.map((g) => <option key={g} value={g}>{g}</option>)}
                </Select></Field>
              )}
            </div>
            {f.classificationSystem !== 'none' && (
              <div className="flex flex-wrap gap-1" aria-hidden>
                {grades.map((g) => (
                  <span key={g} className={cn('rounded px-1.5 py-0.5 text-[11px] tabular-nums', g === f.classificationGrade ? 'bg-primary text-primary-fg' : 'bg-surface-muted text-fg-subtle')}>{g}</span>
                ))}
              </div>
            )}
          </div>
        </Card>
        <Card>
          <CardHeader title="Área doadora" />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            <Field label="Densidade (UF/cm²)"><Input type="number" min={0} max={200} value={f.donorArea.densityPerCm2 ?? ''} disabled={locked} onChange={(x) => setDonor('densityPerCm2', num(x.target.value))} /></Field>
            <Field label="Fios por unidade"><Input type="number" min={1} max={5} step={0.1} value={f.donorArea.hairsPerUnit ?? ''} disabled={locked} onChange={(x) => setDonor('hairsPerUnit', num(x.target.value))} /></Field>
            <Field label="Calibre"><Select value={f.donorArea.caliber ?? ''} disabled={locked} onChange={(x) => setDonor('caliber', (x.target.value || null) as EvaluationFields['donorArea']['caliber'])}>
              <option value="">—</option><option value="thin">Fino</option><option value="medium">Médio</option><option value="thick">Grosso</option>
            </Select></Field>
            <Field label="Qualidade"><Select value={f.donorArea.quality ?? ''} disabled={locked} onChange={(x) => setDonor('quality', (x.target.value || null) as EvaluationFields['donorArea']['quality'])}>
              <option value="">—</option><option value="poor">Ruim</option><option value="fair">Regular</option><option value="good">Boa</option><option value="excellent">Excelente</option>
            </Select></Field>
            <div className="sm:col-span-2">
              <YesNo label="Área doadora suficiente para o plano" value={f.donorArea.sufficient} onChange={(v) => setDonor('sufficient', v)} disabled={locked} />
            </div>
          </div>
        </Card>
      </div>
      <Card>
        <CardHeader title="Plano" />
        <div className="grid gap-4 p-5 md:grid-cols-4">
          <Field label="Enxertos (mín.)"><Input type="number" min={0} max={10000} step={50} value={f.graftsEstimateMin ?? ''} disabled={locked} onChange={(x) => set('graftsEstimateMin', num(x.target.value))} /></Field>
          <Field label="Enxertos (máx.)" error={errs.graftsEstimateMax}><Input type="number" min={0} max={10000} step={50} value={f.graftsEstimateMax ?? ''} disabled={locked} onChange={(x) => set('graftsEstimateMax', num(x.target.value))} /></Field>
          <Field label="Técnica"><Select value={f.technique ?? ''} disabled={locked} onChange={(x) => set('technique', (x.target.value || null) as EvaluationFields['technique'])}>
            <option value="">—</option>{TECHNIQUES.map((t) => <option key={t} value={t}>{t === 'other' ? 'Outra' : t}</option>)}
          </Select></Field>
          <Field label="Indicação" error={errs.indication}><Select value={f.indication ?? ''} disabled={locked} onChange={(x) => set('indication', (x.target.value || null) as EvaluationFields['indication'])}>
            <option value="">—</option>{INDICATIONS.map((t) => <option key={t} value={t}>{INDICATION_LABELS[t]}</option>)}
          </Select></Field>
          <div className="md:col-span-4">
            <Field label="Notas do médico" hint="Cifradas. Não aparecem para a recepção nem no portal do paciente.">
              <Area label="Notas do médico" value={f.notes} onChange={(v) => set('notes', v)} disabled={locked} rows={4} />
            </Field>
          </div>
        </div>
      </Card>
      {err && !Object.keys(errs).length && <Alert tone="danger">{err.message}</Alert>}
      {e.status === 'draft' && write && (
        <DraftActions dirty={dirty} saving={save.isPending} onSave={() => save.mutate()} signing={doSign.isPending} onSign={() => doSign.mutate()}
          onDelete={() => del.mutate()} canSign={sign} signHint="Indicação e grau são obrigatórios para assinar. Você passa a ser o médico responsável pela avaliação." />
      )}
      {e.status === 'signed' && <Addenda entity="evaluations" id={e.id} items={e.addenda} onAdded={() => refresh()} />}
    </div>
  );
}

function RecordsTab({ patientId, kind }: { patientId: string; kind: 'anamnese' | 'avaliacao' }) {
  const qc = useQueryClient();
  const { write } = usePerms();
  const { data: records } = useQuery(recordsQuery(patientId));
  const list = kind === 'anamnese' ? records?.anamneses : records?.evaluations;
  const [picked, setPicked] = useState<string | null>(null);
  const current = picked && list?.some((r) => r.id === picked) ? picked : list?.[0]?.id ?? null;
  const create = useMutation({
    mutationFn: (): Promise<{ id: string }> => (kind === 'anamnese'
      ? api<Anamnesis>(`/patients/${patientId}/anamneses`, { method: 'POST', body: anamnesisAnswersSchema.parse({}) })
      : api<Evaluation>(`/patients/${patientId}/evaluations`, { method: 'POST', body: {} })),
    onSuccess: (r) => { setPicked(r.id); invalidatePatient(qc, patientId); },
  });
  const ana = useQuery({ ...anamnesisQuery(current ?? ''), enabled: kind === 'anamnese' && !!current });
  const ev = useQuery({ ...evaluationQuery(current ?? ''), enabled: kind === 'avaliacao' && !!current });
  const hasDraft = list?.some((r) => r.status === 'draft');

  if (!records) return <Spinner className="size-5 text-fg-subtle" label="Carregando" />;
  return (
    <div className="grid gap-5">
      <RecordPicker items={list ?? []} value={current} onChange={setPicked} onNew={() => create.mutate()} canCreate={write && !hasDraft}
        label={kind === 'anamnese' ? 'Nova anamnese' : 'Nova avaliação'} />
      {create.error && <Alert tone="danger">{create.error.message}</Alert>}
      {!current ? (
        <Card className="grid place-items-center gap-3 p-10 text-center">
          {kind === 'anamnese' ? <ClipboardList className="size-6 text-fg-subtle" /> : <Stethoscope className="size-6 text-fg-subtle" />}
          <p className="font-semibold text-fg">{kind === 'anamnese' ? 'Sem anamnese' : 'Sem avaliação capilar'}</p>
          <p className="max-w-md text-sm text-fg-muted">
            {kind === 'anamnese' ? 'Preencha com o paciente antes da avaliação. Respostas de risco viram alertas para o médico.'
              : 'Classificação, área doadora e estimativa de enxertos. A avaliação assinada libera o orçamento.'}
          </p>
          {write && <Button onClick={() => create.mutate()} loading={create.isPending}>{kind === 'anamnese' ? 'Começar anamnese' : 'Começar avaliação'}</Button>}
        </Card>
      ) : kind === 'anamnese' ? (
        ana.data ? <AnamnesisForm key={`${ana.data.id}-${ana.data.status}`} a={ana.data} patientId={patientId} onDeleted={() => setPicked(null)} /> : <Spinner className="size-5 text-fg-subtle" label="Carregando" />
      ) : (
        ev.data ? <EvaluationForm key={`${ev.data.id}-${ev.data.status}`} e={ev.data} patientId={patientId} onDeleted={() => setPicked(null)} /> : <Spinner className="size-5 text-fg-subtle" label="Carregando" />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ página
export function PatientPage({ id, tab }: { id: string; tab: PatientTab }) {
  const navigate = useNavigate();
  const { data: p, error } = useQuery(patientQuery(id));
  const { data: records } = useQuery(recordsQuery(id));
  const setTab = (aba: PatientTab) => void navigate({ to: '/pacientes/$id', params: { id }, search: aba === 'resumo' ? {} : { aba }, replace: true });
  if (error) return <Alert tone="danger" className="mx-auto max-w-xl">{error.message}</Alert>;
  if (!p) return <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" />;
  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/pacientes" className="inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg"><ArrowLeft className="size-3.5" />Pacientes</Link>
      <header className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[1.9rem] leading-tight text-fg sm:text-[2.2rem]">{p.name}</h1>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
            <span className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[12px]">{p.pseudonym}</span>
            {p.age != null && <span>{p.age} anos</span>}
            {p.sexAtBirth && p.sexAtBirth !== 'not_informed' && <span>{SEX_LABELS[p.sexAtBirth]}</span>}
            {p.lastEvaluation?.classification && <span>{p.lastEvaluation.classification}</span>}
          </p>
        </div>
        <RedFlags flags={p.redFlags} />
      </header>
      <Tabs className="mt-6" value={tab} onChange={setTab} tabs={[
        { value: 'resumo', label: 'Resumo' },
        { value: 'anamnese', label: 'Anamnese', ...(records ? { count: records.anamneses.length } : {}) },
        { value: 'avaliacao', label: 'Avaliação capilar', ...(records ? { count: records.evaluations.length } : {}) },
        { value: 'fotos', label: 'Fotos' },
        { value: 'documentos', label: 'Documentos' },
        { value: 'cirurgia', label: 'Cirurgia' },
      ]} />
      <div className="mt-6">
        {tab === 'resumo' ? <Summary p={p} onOpen={setTab} /> : tab === 'fotos' ? <PhotosTab patientId={id} /> : tab === 'documentos' ? <PatientDocumentsTab patientId={id} /> : tab === 'cirurgia' ? <SurgeryTab patientId={id} /> : <RecordsTab key={tab} patientId={id} kind={tab} />}
      </div>
    </div>
  );
}
